import {getTestProps} from '@benchling/js-test-utils';
import {Anchor, Box, createStyles, Kbd, Paper, Stack, Text} from '@mantine/core';
import React, {useCallback, useLayoutEffect, useState} from 'react';

import useResizeObserver from 'ui-library/hooks/useResizeObserver';

import {PendingCut} from '../EditorTypes';

interface OverlayBounds {
  top: number;
  left: number;
  width: number;
  height: number;
}

function measureBounds(container: HTMLElement, pendingCuts: Array<PendingCut>): OverlayBounds | null {
  if (pendingCuts.length === 0) {
    return null;
  }

  const containerRect = container.getBoundingClientRect();
  let top = Infinity;
  let bottom = -Infinity;
  let maxRight = containerRect.left;

  for (const pendingCut of pendingCuts) {
    const element = container.querySelector(`[data-scroll-id="${pendingCut.cid}"]`);
    if (element == null) {
      return null;
    }

    const rect = element.getBoundingClientRect();
    top = Math.min(top, rect.top);
    bottom = Math.max(bottom, rect.bottom);
    maxRight = Math.max(maxRight, rect.right);
  }

  return {
    top: top - containerRect.top,
    left: 0,
    width: Math.max(containerRect.width, maxRight - containerRect.left),
    height: bottom - top,
  };
}

const useOverlayStyles = createStyles((theme) => ({
  overlay: {
    position: 'absolute',
    zIndex: 8,
    pointerEvents: 'auto',
    cursor: 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: `2px dashed ${theme.colors.blue[6]}`,
    outlineOffset: -2,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.fn.rgba(theme.colors.blue[0], 0.8),
  },
  overlayCard: {
    borderColor: theme.colors.blue[4],
  },
}));

/**
 * Overlay rendered at the Editor level that spans all pending-cut items.
 * Vertical bounds follow pending-cut items; horizontal bounds stay aligned with the
 * editor left edge and extend right only when cut content overflows the container.
 */
export function PendingCutOverlay({
  pendingCuts,
  editorRef,
  onCancel,
}: {
  pendingCuts: Array<PendingCut>;
  editorRef: HTMLDivElement | null;
  onCancel: () => void;
}): JSX.Element {
  const {classes} = useOverlayStyles();
  const [bounds, setBounds] = useState<OverlayBounds | null>(null);
  const {width, height} = useResizeObserver<HTMLDivElement>({ref: editorRef});

  const measure = useCallback(() => {
    if (editorRef == null) {
      setBounds(null);
      return;
    }
    setBounds(measureBounds(editorRef, pendingCuts));
  }, [editorRef, pendingCuts]);

  useLayoutEffect(() => {
    measure();
  }, [measure, width, height]);

  return (
    <Box
      {...getTestProps({component: 'PendingCutOverlay', element: 'root'})}
      className={classes.overlay}
      style={bounds != null ? bounds : {display: 'none'}}
      tabIndex={0}
    >
      <Paper shadow="sm" radius="md" p="sm" px="lg" withBorder={true} className={classes.overlayCard}>
        <Stack spacing={2} align="center">
          <Text size="sm" weight={600} color="gray.7">
            Paste to move content
          </Text>
          <Text size="sm" color="dimmed">
            <Kbd size="sm" sx={{verticalAlign: 'middle'}}>
              Esc
            </Kbd>{' '}
            to{' '}
            <Anchor component="button" type="button" size="sm" onClick={onCancel}>
              cancel
            </Anchor>
          </Text>
        </Stack>
      </Paper>
    </Box>
  );
}
